// @flow
import moment from 'moment';

import graphQLClient from 'Utils/graphql-client/graphqlClient';
import { STATE_PRODUCT_CALENDAR } from 'Utils/request/requestDictionary';
import { QUERY_DEPARTURE_DATE } from 'Utils/uri/constants';
import { getSpecificQueryParam } from 'Utils/uri/uriUtils';

const NO_TRANSPORT_VALUE = 'NO';
const DATE_FORMAT = 'YYYYMM';
export const INITIAL_MONTH = 0;

export type FetchCalendarOptions = {
  duration: number,
  resortId: string,
  transport: string,
  urgentisationRate: number,
};

export type CalendarData = {
  basePrice: ProductPrice | null,
  blocks: null | Array<{
    hasPrice: null | boolean,
    id: null | string,
    name: null | string,
    price: null | string,
    shortName: null | string,
    year: null | string,
  }>,
  firstAvailableMonthIndex: null | number,
  months: null | Array<{
    id: null | string,
    days: null | Array<{
      dayContent: null | {
        arrivalDate: null | string,
        availability: null | string,
        canGoToBooking: null | boolean,
        id: null | string,
        isCurrentMonth: null | boolean,
        name: null | string,
        number: null | string,
        offer: null | string,
        shortName: null | string,
      },
      priceConfig: null | {
        bestPrice: null | { formattedValue: null | string },
        currency: null | string,
        initialPrice: null | { formattedValue: null | string },
        isBestPriceMonth: null | boolean,
        termsAndConditions: null | {
          accommodationComfortLabel: null | string,
          departureCity: null | {
            value: null | string,
            name: null | string,
          },
          departureDate: null | string,
          duration: null | number,
          numberOfAdults: null | number,
        },
        rawUnit: null | string,
        unit: null | string,
      },
    }>,
  }>,
  weekDays: null | Array<string>,
};

export type PriceOfDay = {
  availability: string,
  available: boolean,
  closed: boolean,
  exclusivity: boolean,
  full: boolean,
  isBestPrice: boolean,
  lastAvailability: boolean,
  noAvailability: boolean,
  noDeparture: boolean,
  noFlight: boolean,
  oldPrice: string,
  price: string,
  unit: string,
};

export type PricesByDays = {
  [date: string]: PriceOfDay,
};

export function calendarQuery(args: {
  departureCity: string,
  duration: number,
  locale: string,
  resortId: string,
  urgentisationRate: number,
}) {
  const { resortId, duration, departureCity, urgentisationRate } = args;
  return `
      calendar(resortId: "${resortId}", duration: "${duration}", departureCity: "${departureCity}") {
        basePrice {
          currency
          bestPrice {
            value
            currency
            formattedValue
          }
          initialPrice {
            value
            currency
            formattedValue
          }
          unit
        }
        blocks {
          hasPrice
          id
          name
          price {
            value
            currency
            formattedValue
          }
          shortName
          year
        }
        firstAvailableMonthIndex
        months {
          id
          days {
            dayContent {
              arrivalDate
              availability
              canGoToBooking
              id
              isCurrentMonth
              name
              number
              offer(urgentisationRate: ${urgentisationRate})
              shortName
            }
            priceConfig {
              bestPrice(forceAdultPerTrip: true) { value formattedValue }
              currency
              initialPrice(forceAdultPerTrip: true) { value formattedValue }
              isBestPriceMonth
              termsAndConditions {
                accommodationComfortLabel
                departureCity {
                  value
                  name
                }
                departureDate
                duration
                numberOfAdults
              }
              rawUnit
              unit
            }
          }
        }
        weekDays
      }
    `;
}

export function queryFetchCalendar(
  appContext: AppContext,
  options: FetchCalendarOptions,
): Promise<{ calendar: CalendarData }> {
  const { locale } = appContext;
  const { resortId = '', transport = '', duration, urgentisationRate } = options;
  const calendarQueryArgs = {
    locale,
    resortId,
    departureCity: transport || NO_TRANSPORT_VALUE,
    duration,
    urgentisationRate,
  };
  return graphQLClient
    .runQuery(`query {${calendarQuery(calendarQueryArgs)}}`, appContext, {
      requestName: STATE_PRODUCT_CALENDAR,
    })
    .then(({ calendar }) => ({
      calendar,
    }));
}

export function getDefaultSelectedMonth(
  value: string | number | null = null,
  format: string | null = null,
) {
  const momentDate = value ? moment(value, format) : moment();
  return parseInt(momentDate.format(DATE_FORMAT), 10);
}

export function getSelectedMonth(calendar: CalendarData, previousSelectedMonth: number): number {
  // The query date param prevails
  const dateQueryParam = getSpecificQueryParam(QUERY_DEPARTURE_DATE);
  if (dateQueryParam) {
    return getDefaultSelectedMonth(dateQueryParam);
  }

  const firstCalendarMonth = calendar.months?.[0].id || getDefaultSelectedMonth();
  const firstAvailableMonthIndex = calendar.firstAvailableMonthIndex || INITIAL_MONTH;
  const firstAvailableMonth = parseInt(
    moment(firstCalendarMonth, DATE_FORMAT)
      .add(firstAvailableMonthIndex, 'months')
      .format(DATE_FORMAT),
    10,
  );

  // The previously selected month remains selected unless the first available dates are later
  if (previousSelectedMonth > firstAvailableMonth) {
    return previousSelectedMonth;
  }

  return firstAvailableMonth;
}

export function getPricesByDays(calendar: CalendarData): PricesByDays {
  const prices = {};

  if (calendar.months) {
    calendar.months.forEach(({ days }) => {
      if (days) {
        days.forEach(({ dayContent, priceConfig }) => {
          const { availability, id, offer } = dayContent || {};
          const isBestPrice = priceConfig?.isBestPriceMonth;
          const unit = priceConfig?.unit;

          if (id) {
            const closed = availability === 'closed';
            const exclusivity = availability === 'exclusivity';
            const noDeparture = availability === 'noDeparture';
            const noFlight = availability === 'noFlight';
            const full = availability === 'full';
            const noAvailability = availability === '';
            const available = availability === 'available' || availability === 'unknown';
            const lastAvailability = offer === 'urgentisation';

            prices[id] = {
              availability,
              available,
              closed,
              exclusivity,
              full,
              isBestPrice,
              lastAvailability,
              noAvailability,
              noDeparture,
              noFlight,
              oldPrice: priceConfig?.initialPrice?.formattedValue || null,
              price: priceConfig?.bestPrice?.formattedValue || null,
              unit,
            };
          }
        });
      }
    });
  }

  return prices;
}
